import { Box, Flex, Text } from '@chakra-ui/react'
import React from 'react'

import { useColorModeValue } from '@/components/ui/color-mode'
import { Tooltip } from '@/components/ui/tooltip'
import { Link } from '@/routing'

import type { NavEntry } from './nav.types'

/**
 * One row of the rail.
 *
 * Two things changed from the row it replaces, and both were bugs rather than taste:
 *
 * - **it stays a link when it is the current page.** The old row rewrote its own target to
 *   `'#'`, so the entry you were standing on could not be clicked, middle-clicked, copied or
 *   opened in a new tab — and screen readers were told the page had a link to nowhere.
 * - **it is told whether it is active**, by the caller, using longest-prefix matching. The old
 *   row decided for itself with strict equality, which meant nothing ever lit up on a deep
 *   page: three levels into a webhook's delivery log, the rail looked idle.
 *
 * `aria-current="page"` carries the state to assistive technology; colour alone never did.
 */
export const RailEntry = ({
  entry,
  active,
  collapsed = false,
}: {
  entry: NavEntry
  active: boolean
  collapsed?: boolean
}) => {
  // The one definition of "the brand colour, filled, with text on it". This row used to pick a
  // pale step by hand and write orange on it, which only ever made sense while the brand was
  // Communo's yellow: a blue network got an active entry in orange.
  const activeBg = 'brand.solid'
  const activeColor = 'brand.contrast'
  const hoverBg = useColorModeValue('gray.100', 'gray.700')
  const disabledColor = useColorModeValue('gray.400', 'gray.600')

  const Icon = entry.icon

  const row = (
    <Flex
      align="center"
      justify={collapsed ? 'center' : 'flex-start'}
      px={collapsed ? 3 : 4}
      py={2.5}
      mx={collapsed ? 2 : 3}
      my={0.5}
      gap={3}
      borderRadius="lg"
      minH="40px"
      role="group"
      cursor={entry.disabled ? 'default' : 'pointer'}
      transition="all 0.15s ease"
      {...(active && !entry.disabled
        ? { bg: activeBg, color: activeColor }
        : {})}
      {...(entry.disabled ? { color: disabledColor } : {})}
      _hover={
        entry.disabled
          ? {}
          : active
            ? { bg: activeBg, color: activeColor }
            : { bg: hoverBg }
      }
    >
      {Icon ? <Icon size={collapsed ? 20 : 17} /> : null}
      {collapsed ? null : (
        <>
          <Text
            fontSize="sm"
            fontWeight={active ? 'semibold' : 'medium'}
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            flex="1"
            minW={0}
          >
            {entry.label}
          </Text>
          {entry.count ? (
            <Text fontSize="xs" fontWeight="bold" opacity={0.7} flexShrink={0}>
              {entry.count}
            </Text>
          ) : null}
          {entry.badge ? <Box flexShrink={0}>{entry.badge}</Box> : null}
        </>
      )}
    </Flex>
  )

  // A disabled entry is shown, never followed: it names something the plan does not carry yet,
  // and a link that silently does nothing is worse than one that plainly cannot be taken.
  const content = entry.disabled ? (
    <Box aria-disabled>{row}</Box>
  ) : (
    <Link
      to={entry.to}
      aria-current={active ? 'page' : undefined}
      style={{ textDecoration: 'none' }}
    >
      {row}
    </Link>
  )

  return collapsed ? (
    <Tooltip content={entry.label} positioning={{ placement: 'right' }}>
      {content}
    </Tooltip>
  ) : (
    content
  )
}
